import { createContext, useContext, useRef } from 'react'

import { TiptapCollabProvider } from '@tiptap-pro/provider'
import { Editor, type JSONContent } from '@tiptap/react'
import * as Y from 'yjs'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import { jsonToMarkdown } from '@/components/common/TextArea/RichTextArea/lib'
import {
  type RichTextAreaStore,
  type RichTextAreaStoreState,
  createRichTextAreaStore,
} from '@/components/common/TextArea/RichTextArea/stores'

interface RichTextAreaContextType {
  editor: Editor | null
  doc: Y.Doc | null
  provider: TiptapCollabProvider | null
  isFocused: boolean
  isSynced: boolean
  getHtml: () => string
  getJson: () => JSONContent
  getMarkdown: () => string
}

interface RichTextAreaContextProvider {
  editor: Editor | null
  doc: Y.Doc | null
  provider: TiptapCollabProvider | null
  isFocused: boolean
  collaborationId: string | undefined
  children: React.ReactNode
  setIsFocused: (isFocused: boolean) => void
}

export const RichTextAreaContext = createContext<RichTextAreaContextType>({
  editor: null,
  doc: null,
  provider: null,
  isFocused: false,
  isSynced: true,
  getHtml: () => '',
  getJson: () => {
    return {}
  },
  getMarkdown: () => '',
})

interface RichTextAreaStoreContextProviderProps {
  children: React.ReactNode
}

export const RichTextAreaStoreContext = createContext<
  RichTextAreaStore | undefined
>(undefined)

export const RichTextAreaStoreContextProvider: React.FC<
  RichTextAreaStoreContextProviderProps
> = ({ children }) => {
  const storeRef = useRef<RichTextAreaStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createRichTextAreaStore()
  }

  return (
    <RichTextAreaStoreContext.Provider value={storeRef.current}>
      {children}
    </RichTextAreaStoreContext.Provider>
  )
}

export const RichTextAreaContextProvider: React.FC<
  RichTextAreaContextProvider
> = ({ editor, doc, provider, isFocused, collaborationId, children }) => {
  const storeRef = useRef<RichTextAreaStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createRichTextAreaStore()
  }

  const getHtml = () => {
    return editor?.getHTML() ?? ''
  }
  const getJson = () => {
    return editor?.getJSON() ?? {}
  }
  const getMarkdown = () => {
    return jsonToMarkdown(editor?.getJSON() ?? {})
  }

  // local editor is always synced,
  // otherwise, if the collaborationId is defined, use the provider's isSynced value
  const isSynced =
    collaborationId === undefined ? true : (provider?.isSynced ?? false)

  return (
    <RichTextAreaContext.Provider
      value={{
        editor,
        provider,
        doc,
        isFocused,
        isSynced,
        getHtml,
        getJson,
        getMarkdown,
      }}
    >
      {children}
    </RichTextAreaContext.Provider>
  )
}

export const useRichTextAreaContext = () => {
  const context = useContext(RichTextAreaContext)
  if (!context) {
    throw new Error(
      'useRichTextAreaContext must be used within a RichTextAreaContextProvider',
    )
  }
  return context
}

export const useRichTextAreaStore = <T,>(
  selector: (state: RichTextAreaStoreState) => T,
) => {
  const store = useContext(RichTextAreaStoreContext)
  if (!store) {
    throw new Error(
      "useRichTextAreaStore couldn't find RichTextAreaStoreContext.Provider in the tree",
    )
  }
  return useStore(store, selector)
}

export const useVersions = () => {
  return useRichTextAreaStore(useShallow((state) => state.versions))
}

export const useSetVersions = () => {
  return useRichTextAreaStore(useShallow((state) => state.setVersions))
}
